import Button from "@app/components/button";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { addToast } from "@app/components/toast/toast.service";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  Role,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import AuthService from "@app/services/http/auth.service";
import { storeUser } from "@app/store/auth";
import { GoogleCredentialJwtPayload, LoginRequest, LoginResponse, SocialLoginRequest, UserData } from "@app/types";
import { loginValidationSchema } from "@app/validations";
import { Images } from "@assets/images";
import StorageService from "@core/services/storage";
import { Environment } from "@environments/environment";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { Form, Formik, FormikContextType } from "formik";
import { jwtDecode } from "jwt-decode";
import { createRef } from "react";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import { ReactFacebookFailureResponse, ReactFacebookLoginInfo } from "react-facebook-login";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import FacebookIcon from "@app/icons/facebook";

function SignIn() {
  const formRef = createRef<FormikContextType<LoginRequest>>();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onSubmit = (values: LoginRequest) => {
    AuthService.login(values.email, values.password).then((res) => {
      const { accessToken, refreshToken, user } = res.data;

      if (user.role !== Role.AUTHOR) {
        addToast({ text: SystemMessage.ACCESS_DENIED, status: "inValid" });

        return;
      }

      const userData: UserData = {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
        fullName: user.fullName,
        thumbnail: user.thumbnail,
        canCreateAudio: Boolean(user.canCreateAudio),
        canUploadAudio: Boolean(user.canUploadAudio),
      };

      StorageService.set(ACCESS_TOKEN_KEY, accessToken);
      StorageService.set(REFRESH_TOKEN_KEY, refreshToken);
      StorageService.setObject(USER_INFO_KEY, userData);
      dispatch(storeUser(user));
      navigate("/");
    });
  };

  const handleLogin = (res: LoginResponse) => {
    const { accessToken, refreshToken, user } = res.data;

    if (user.role !== Role.AUTHOR) {
      addToast({ text: SystemMessage.ACCESS_DENIED, status: "inValid" });

      return;
    }

    const userData: UserData = {
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role,
      fullName: user.fullName,
      thumbnail: user.thumbnail,
      canCreateAudio: Boolean(user.canCreateAudio),
      canUploadAudio: Boolean(user.canUploadAudio),
    };

    StorageService.set(ACCESS_TOKEN_KEY, accessToken);
    StorageService.set(REFRESH_TOKEN_KEY, refreshToken);
    StorageService.setObject(USER_INFO_KEY, userData);
    dispatch(storeUser(user));
    navigate("/");
  };

  const onFacebookLoginSuccess = (
    userInfo: ReactFacebookLoginInfo | ReactFacebookFailureResponse
  ) => {
    if ((userInfo as ReactFacebookFailureResponse)?.status === "unknown") {
      addToast({ text: SystemMessage.LOGIN_FAILED, status: "inValid" });

      return;
    }

    const data = userInfo as ReactFacebookLoginInfo;

    const body: SocialLoginRequest = {
      email: data.email ?? "",
      fullName: data.name ?? "",
      idToken: data.accessToken,
      provider: "FACEBOOK",
      picture: data.picture?.data?.url ?? "",
    };

    AuthService.loginSocial(body).then((res) => {
      handleLogin(res);
    });
  };

  const onGoogleLoginError = () => {
    addToast({ text: SystemMessage.LOGIN_FAILED, status: "inValid" });
  };

  const onGoogleLoginSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      const decoded = jwtDecode<GoogleCredentialJwtPayload>(
        credentialResponse.credential
      );

      const body: SocialLoginRequest = {
        email: decoded.email,
        fullName: decoded.name,
        idToken: credentialResponse.credential,
        provider: "GOOGLE",
        picture: decoded.picture ?? "",
      };

      AuthService.loginSocial(body).then((res) => {
        handleLogin(res);
      });
    }
  };

  return (
    <div className="bg-color w-screen h-screen flex items-center justify-center">
      <div className="w-[443px] h-auto px-[34px] py-11 bg-white rounded-xl">
        <div className="flex justify-center">
          <img className="w-28" src={Images.LogoText.default} alt="" />
        </div>
        <div className="mt-6 font-semibold text-xl text-center neutral-n80 font-[SVN-Gilroy]">
          Chào mừng trở lại!
        </div>
        <div className="mt-2 black-30 text-sm text-center black-30 font-[SVN-Gilroy] font-medium">
          Đăng nhập vào tài khoản của bạn
        </div>
        <Formik
          innerRef={formRef}
          initialValues={{ email: "", password: "" }}
          onSubmit={onSubmit}
          validationSchema={loginValidationSchema}
          validateOnBlur
          validateOnChange
        >
          <Form>
            <div className="mt-[60px]">
              <FormControl name="email">
                <Input
                  placeholder="Email/Username"
                  inputIcon={Images.MessageIcon.default}
                  inputIconClassName="!w-[18px] !h-[18px]"
                  inputClassName="font-[SVN-Gilroy] font-medium !text-[#272C36] tracking-[0.024px]"
                  errorClassName="font-[SVN-Gilroy] tracking-[0.024px]"
                />
              </FormControl>
            </div>
            <div className="mt-3">
              <FormControl name="password">
                <Input
                  type="password"
                  placeholder="Password"
                  inputIcon={Images.SecurityIcon.default}
                  inputIconClassName="!w-[18px] !h-[18px]"
                  inputClassName="font-[SVN-Gilroy] font-medium !text-[#272C36] tracking-[0.024px]"
                  errorClassName="font-[SVN-Gilroy] tracking-[0.024px]"
                />
              </FormControl>
            </div>
            <Button
              type="submit"
              containerClassName="mt-12 mx-auto"
              label="Đăng nhập"
              labelClassName="font-[SVN-Gilroy]"
              width=""
              size="l"
              className="text-lg font-bold px-11"
            />
            <div className="flex items-center justify-center my-6 px-10">
              <hr className="flex-1 border-t border-gray-300" />
              <span className="mx-2 text-gray-500 text-sm font-[SVN-Gilroy]">
                Or continue with
              </span>
              <hr className="flex-1 border-t border-gray-300" />
            </div>
            <div className="flex space-x-4 my-6 justify-center items-center">
              <FacebookLogin
                appId={Environment.FACEBOOK_APP_ID}
                fields="name,email,picture"
                callback={onFacebookLoginSuccess}
                render={({ onClick }) => (
                  <button
                    type="button"
                    className="flex items-center justify-center space-x-2 px-4 py-2 h-10 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100 w-[141px]"
                    onClick={onClick}
                  >
                    <FacebookIcon />
                    <span className="font-[SVN-Gilroy] font-semibold text-[14px]">
                      Facebook
                    </span>
                  </button>
                )}
              />
              <GoogleLogin
                onSuccess={onGoogleLoginSuccess}
                onError={onGoogleLoginError}
                shape="pill"
                theme="outline"
                size="large"
                text="signin"
                width={141}
              />
            </div>
          </Form>
        </Formik>
      </div>
    </div>
  );
}

export default SignIn;
